import {useCallback, useEffect, useRef, useState} from "react";
import {Email} from "models/Email";
import {EmailSearchPage, searchEmails} from "services/Api";

export function useEmailSearch(query: string, box: string) {
    const keyword = query.trim().toLowerCase();
    const [emails, setEmails] = useState<Email[]>([]);
    const [page, setPage] = useState<EmailSearchPage | null>(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(false);
    const controllerRef = useRef<AbortController | null>(null);
    const retryCursor = useRef<string | undefined>();

    const scanWindow = useCallback(async (cursor?: string) => {
        controllerRef.current?.abort();
        const controller = new AbortController();
        controllerRef.current = controller;
        setLoading(true);
        setError(false);
        try {
            let next = cursor;
            do {
                retryCursor.current = next;
                const result = await searchEmails(keyword, box, next, controller.signal);
                if (controller.signal.aborted) return;
                // Keep results separate from the ordinary inbox cache and its
                // oldest/newest pagination boundaries.
                setEmails(current => {
                    const merged = new Map(current.map(email => [email.id, email]));
                    result.emails.forEach(email => merged.set(email.id, email));
                    return Array.from(merged.values()).sort((a, b) => b.date - a.date || b.id.localeCompare(a.id));
                });
                setPage({...result, emails: []});
                next = result.window_complete ? undefined : result.next_cursor;
            } while (next && !controller.signal.aborted);
        } catch {
            if (!controller.signal.aborted) setError(true);
        } finally {
            if (!controller.signal.aborted) setLoading(false);
        }
    }, [keyword, box]);

    useEffect(() => {
        controllerRef.current?.abort();
        setEmails([]);
        setPage(null);
        setError(false);
        setLoading(keyword !== "");
        retryCursor.current = undefined;
        if (!keyword) return;
        const timeout = window.setTimeout(() => { void scanWindow(); }, 300);
        return () => {
            window.clearTimeout(timeout);
            controllerRef.current?.abort();
        };
    }, [keyword, box, scanWindow]);

    const searchOlder = () => {
        if (!loading && !error && page?.window_complete && page.older_cursor) {
            void scanWindow(page.older_cursor);
        }
    };
    const retry = () => { if (!loading) void scanWindow(retryCursor.current); };
    const removeEmails = (ids: string[]) => setEmails(current => current.filter(email => !ids.includes(email.id)));
    const updateEmail = (updated: Email) => setEmails(current => current
        .map(email => email.id === updated.id ? updated : email)
        .filter(email => box !== "starred" || email.starred));

    return {emails, page, loading, error, searchOlder, retry, removeEmails, updateEmail};
}
